<template>
    <div>
        <!-- start page title area-->
        <div class="page-title-area ptb-100">
            <div class="container">
                <div class="page-title-content">
                    <h1>{{$t('contact_us')}}</h1>
                </div>
			</div>
			<div class="bg-image">
				<img :src="banner_image" >
			</div>
        </div>
        <!-- end page title area -->

        <!-- start contact section -->
        <section id="contact" class="contact-section contact-single pt-100 pb-70">
            <div class="container">
                <div class="top-content">
                    <div class="title">
                        <span class="sub-title">{{content.small_title}}</span>
                        <h2>{{content.large_title}}</h2>
                    </div>
                    <ul class="info-list">
                        <li><i class='bx bx-phone'></i>{{content.call_title}}</li>
                        <li><a :href="'tel:'+content.phone">{{content.phone}}</a></li>
                    </ul>
                    <ul class="info-list">
                        <li><i class='bx bx-envelope'></i>{{content.mail_title}}</li>
                        <li><a :href="'mailto:'+content.email">{{content.email}}</a></li>
                    </ul>
                </div>
                <div class="row align-items-center">
                    <div class="col-lg-6">
                        <div class="contact-image mb-30">
                            <img :src="content.image" />
                        </div>
                    </div>
                    <div class="col-lg-6 mb-30">
                        <div ref="contactForm">
                            <!-- Global status banners -->
                            <div v-if="errors.success" class="cf-alert cf-alert-success">{{ errors.success }}</div>
                            <div v-if="errors.submit" class="cf-alert cf-alert-error">{{ errors.submit }}</div>

                            <form @submit="onSubmit" id="contactForm" novalidate>
                                <div class="row">
                                    <div class="col-lg-6 col-md-6">
                                        <div class="form-group">
                                            <input type="text" name="name" v-model="form.name" class="form-control" :class="{ 'is-invalid': errors.name }" id="name" :placeholder="$t('your_name')" />
                                            <small v-if="errors.name" class="cf-error">{{ errors.name }}</small>
                                        </div>
                                    </div>
                                    <div class="col-lg-6 col-md-6">
                                        <div class="form-group">
                                            <input type="email" name="email" v-model="form.email" class="form-control" :class="{ 'is-invalid': errors.email }" id="email" :placeholder="$t('email_address')" />
                                            <small v-if="errors.email" class="cf-error">{{ errors.email }}</small>
                                        </div>
                                    </div>
                                    <div class="col-lg-6 col-md-6">
                                        <div class="form-group">
                                            <input type="text" @keypress="isNumber($event)" name="phone_number" v-model="form.phone" class="form-control" :class="{ 'is-invalid': errors.phone }" id="phone_number" :placeholder="$t('phone_number')" />
                                            <small v-if="errors.phone" class="cf-error">{{ errors.phone }}</small>
                                        </div>
                                    </div>
                                    <div class="col-lg-6 col-md-6">
                                        <div class="form-group">
                                            <input type="text" name="subject" v-model="form.subject" class="form-control" :class="{ 'is-invalid': errors.subject }" id="subject" :placeholder="$t('subject')" />
                                            <small v-if="errors.subject" class="cf-error">{{ errors.subject }}</small>
                                        </div>
                                    </div>
                                    <div class="col-lg-12 col-md-12">
                                        <div class="form-group">
                                            <textarea name="message" id="message" v-model="form.message" class="form-control" :class="{ 'is-invalid': errors.message }" cols="30" rows="8" :placeholder="$t('your_message_alt')"></textarea>
                                            <small v-if="errors.message" class="cf-error">{{ errors.message }}</small>
                                        </div>
                                    </div>
                                    <div class="col-lg-12 col-md-12">
                                        <button type="submit" class="primary-btn" :disabled="submitting">
                                            {{ submitting ? $t('submitting') : $t('submit') }}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <!-- end contact section -->
    </div>
</template>

<script>
import axios from "../axios";
import { mapActions } from 'vuex';

export default {
    name: "ContactUs",

    data() {
        return {
            content: {},
            banner_image: '',
            submitting: false,
            errors: {},
            form: {
                name: "",
                email: "",
                phone: "",
                message: "",
                subject: ""
            }
        }
    },

    watch: {
        // Live-clear each field's error the instant it becomes valid, rather
        // than waiting for the next Submit click. Only touches fields that
        // already have an error showing (see recheckField).
        'form.name'(){ this.recheckField('name'); },
        'form.email'(){ this.recheckField('email'); },
        'form.phone'(){ this.recheckField('phone'); },
        'form.subject'(){ this.recheckField('subject'); },
        'form.message'(){ this.recheckField('message'); }
    },

    methods:{
        ...mapActions("navigation", ["setNavLink"]),
        ...mapActions("navigation", ["setNavItem"]),

        isNumber: function(evt) {
            evt = (evt) ? evt : window.event;
            var charCode = (evt.which) ? evt.which : evt.keyCode;
            if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
                evt.preventDefault();
            } else {
                return true;
            }
        },

        scrollToFormTop(){
            this.$nextTick(() => {
                if(this.$refs.contactForm){
                    this.$refs.contactForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            });
        },

        // Scrolls to and focuses the first field currently showing a validation
        // error, so the user lands directly on what needs fixing.
        focusFirstInvalidField(){
            this.$nextTick(() => {
                if(!this.$refs.contactForm) return;
                const el = this.$refs.contactForm.querySelector('.is-invalid');
                if(!el) return;

                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                if(typeof el.focus === 'function'){
                    el.focus({ preventScroll: true });
                }
            });
        },

        // Single source of truth for a field's validity, reused by both
        // validateForm (on submit) and recheckField (live, as the user types).
        validateField(field){
            const f = this.form;
            switch(field){
                case 'name':
                    return f.name.trim() ? '' : (this.$t('name_required') || 'Please enter your name.');
                case 'email':
                    return (f.email.trim() && /^\S+@\S+\.\S+$/.test(f.email)) ? '' : (this.$t('valid_email_required') || 'Please enter a valid email address.');
                case 'phone':
                    return f.phone.trim() ? '' : (this.$t('phone_required') || 'Please enter your phone number.');
                case 'subject':
                    return f.subject.trim() ? '' : (this.$t('subject_required') || 'Please enter a subject.');
                case 'message':
                    return f.message.trim() ? '' : (this.$t('message_required') || 'Please enter your message.');
                default:
                    return '';
            }
        },

        validateForm(){
            const merged = {};
            let valid = true;

            ['name', 'email', 'phone', 'subject', 'message'].forEach(field => {
                const msg = this.validateField(field);
                if(msg){
                    merged[field] = msg;
                    valid = false;
                }
            });

            this.errors = merged;
            return valid;
        },

        // Called by the watchers above. Only re-checks a field that already has
        // an error showing, so nothing appears before the first Submit attempt.
        recheckField(field){
            if(this.errors[field] === undefined) return;

            const msg = this.validateField(field);
            const merged = { ...this.errors };
            if(msg){
                merged[field] = msg;
            } else {
                delete merged[field];
            }
            this.errors = merged;
        },

        onSubmit(e){
            e.preventDefault();

            if(!this.validateForm()){
                this.focusFirstInvalidField();
                return;
            }

            this.submitting = true;

            let formData = new FormData();
            formData.append('name', this.form.name);
            formData.append('email', this.form.email);
            formData.append('phone', this.form.phone);
            formData.append('message', this.form.message);
            formData.append("subject", this.form.subject);

            axios.post(`contact-us`, formData, {headers: {"Content-Type": "multipart/form-data"}})
            .then(response => {
                if(response.data.status){
                    this.errors = { success: this.$t('form_submitted') || 'Your message has been sent successfully.' };
                    e.target.reset();
                    this.form = { name: "", email: "", phone: "", message: "", subject: "" };
                    this.scrollToFormTop();
                }else{
                    this.errors = { submit: this.$t('invalid_form_details') || 'Invalid form details. Please check your inputs.' };
                    this.scrollToFormTop();
                }
            })
            .catch(err => {
                if(err.response && err.response.status === 422 && err.response.data.errors){
                    const backendErrors = err.response.data.errors;
                    const merged = { ...this.errors };
                    Object.keys(backendErrors).forEach(key => {
                        merged[key] = backendErrors[key][0];
                    });
                    this.errors = merged;
                } else {
                    this.errors = { submit: this.$t('submission_failed') || 'Something went wrong. Please try again.' };
                }
                this.scrollToFormTop();
            })
            .finally(() => {
                this.submitting = false;
            })
        }
    },

    created() {
        this.setNavLink('contact_us')
        this.setNavItem('')

        axios.get(`contact-us`)
        .then(response => {
            this.content = response.data.data.content
            this.banner_image = response.data.data.banner_image
        })
        .catch(e => {
            console.error(e);
        })
    }
}
</script>

<style scoped>
.form-group {
    margin-bottom: 24px;
}

.contact-single .form-control {
    height: 44px;
    padding: 10px 14px;
    font-size: 14px;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    box-sizing: border-box;
}

.contact-single textarea.form-control {
    height: auto;
    min-height: 160px;
    padding: 12px 14px;
}

.form-control.is-invalid {
    border-color: #d9534f;
}

.cf-error {
    display: block;
    color: #d9534f;
    font-size: 12px;
    margin-top: 4px;
}

.cf-alert {
    padding: 12px 16px;
    border-radius: 3px;
    margin-bottom: 20px;
    font-size: 14px;
}
.cf-alert-error {
    background: #fdecea;
    color: #d9534f;
    border: 1px solid #f5c2c0;
}
.cf-alert-success {
    background: #eafaf0;
    color: #28a745;
    border: 1px solid #b7ebc6;
}

.primary-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>